import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import "@/App.css";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import SolutionsGrid from "@/components/SolutionsGrid";
import AssessmentPitch from "@/components/AssessmentPitch";
import Method from "@/components/Method";
import About from "@/components/About";
import Testimonials from "@/components/Testimonials";
import FAQSection from "@/components/FAQSection";
import Booking from "@/components/Booking";
import Mindset from "@/components/Mindset";
import Footer from "@/components/Footer";
import { AuthProvider, useAuth } from "@/crm/AuthContext";
import { ThemeProvider } from "@/crm/ThemeContext";
import Login from "@/crm/Login";
import CrmLayout from "@/crm/CrmLayout";
import Dashboard from "@/crm/Dashboard";
import Contacts from "@/crm/Contacts";
import ContactDetail from "@/crm/ContactDetail";
import Pipeline from "@/crm/Pipeline";
import CasesPage from "@/crm/CasesPage";
import Clients from "@/crm/Clients";
import Followups from "@/crm/Followups";
import Campaigns from "@/crm/Campaigns";
import Assessment from "@/assessment/Assessment";
import Sign from "@/crm/Sign";
import SignNew from "@/crm/SignNew";
import SignEnvelope from "@/crm/SignEnvelope";
import SignPublic from "@/crm/SignPublic";
import Privacy from "@/legal/Privacy";
import Terms from "@/legal/Terms";
import Disclosures from "@/legal/Disclosures";
import Accessibility from "@/legal/Accessibility";
import Complaints from "@/legal/Complaints";

function Landing() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 300);
    return () => clearTimeout(t);
  }, [hash]);
  return (
    <div className="App bg-white" data-testid="app-root">
      <Navbar />
      <main>
        <Hero />
        <SolutionsGrid />
        <Method />
        <AssessmentPitch />
        <About />
        <Testimonials />
        <FAQSection />
        <Mindset />
        <Booking />
      </main>
      <Footer />
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-white/50 text-sm"
        style={{ background: "#050C24" }}
      >
        Checking session…
      </div>
    );
  }
  if (!user || !user.email) {
    return <Navigate to="/login" replace state={{ from: loc }} />;
  }
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/assessment" element={<Assessment />} />
          <Route path="/login" element={<Login />} />
          <Route path="/sign/:token" element={<SignPublic />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/disclosures" element={<Disclosures />} />
          <Route path="/legal/accessibility" element={<Accessibility />} />
          <Route path="/legal/complaints" element={<Complaints />} />
          <Route
            path="/crm"
            element={
              <Protected>
                <ThemeProvider>
                  <CrmLayout />
                </ThemeProvider>
              </Protected>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="clients" element={<Clients />} />
            <Route path="followups" element={<Followups />} />
            <Route path="pipeline" element={<Pipeline />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="contacts/:contactId" element={<ContactDetail />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="cases" element={<CasesPage />} />
            <Route path="sign" element={<Sign />} />
            <Route path="sign/new" element={<SignNew />} />
            <Route path="sign/:id" element={<SignEnvelope />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
